import { Component, computed, Signal, inject, effect } from '@angular/core';
import {
    Breadcrumb,
    BreadcrumbListComponent,
    buildFolderBreadcrumbs,
    TitledContainerComponent,
} from '@r1-compass-library/components';
import { combineLatest, map } from 'rxjs';
import { rxEffect } from 'ngxtension/rx-effect';
import { filterNil } from 'ngxtension/filter-nil';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { RoutingService } from '@r1-compass-library/services';
import { provideComponentStore } from '@ngrx/component-store';
import { DataMartTablesStore } from '../template-criteria/store/data-mart-tables.store';
import { SelectedContentAssetStore } from './store/selected-content-asset.store';
import { ContentAssetListStore } from './store/content-asset-list.store';
import { AppConstants } from '../app.constants';
import { DynamicContentStore } from '../plays/play-talking-points/dynamic-content.store';
import { ContentLibraryValidationService } from './content-library-validation.service';
import { ContentLibraryValidationStateService } from './content-library-validation-state.service';
import { CompassRouteParent, ContentAsset } from '@r1-compass-library/models';
import {
    SyncValidationStateTopLevelComponent,
    SyncValidationStateService,
    ContentLibrarySectionName,
} from '@r1-compass-library/features/validation-sync';

@Component({
    selector: 'r1-compass-app-content-library',
    imports: [TitledContainerComponent, RouterModule, BreadcrumbListComponent],
    providers: [
        provideComponentStore(SelectedContentAssetStore),
        provideComponentStore(ContentAssetListStore),
        provideComponentStore(DataMartTablesStore),
        provideComponentStore(DynamicContentStore),
    ],
    template: `
        @let asset = selectedContentAssetStore.asset();
        @if (showCloseButton() && asset) {
            <div class="additional-content-asset-info-bar">
                <r1-compass-lib-breadcrumb-list [breadcrumbList]="breadcrumbs()" />
                <p [style.color]="asset.enabled ? 'green' : 'red'">
                    {{ asset.enabled ? 'Enabled' : 'Disabled' }}
                </p>
            </div>
        }
        <r1-compass-lib-titled-container
            [title]="containerTitle()"
            [showCloseButton]="showCloseButton()"
            (close)="onClosed()">
            <router-outlet />
        </r1-compass-lib-titled-container>
    `,
    styles: `
        .additional-content-asset-info-bar {
            display: flex;
            justify-content: space-between;
        }
        p {
            margin-top: 0;
        }
    `,
})
export class ContentLibraryComponent
    implements SyncValidationStateTopLevelComponent<ContentLibrarySectionName, ContentAsset>
{
    protected readonly routingService = inject(RoutingService);
    readonly selectedContentAssetStore = inject(SelectedContentAssetStore);
    readonly contentAssetListStore = inject(ContentAssetListStore);
    readonly #route = inject(ActivatedRoute);
    readonly #router = inject(Router);
    readonly #validationService = inject(ContentLibraryValidationService);
    readonly #validationStateService = inject(ContentLibraryValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);

    constructor() {
        effect(
            () => {
                const assetId = this.routingService.assetId();
                if (assetId && assetId !== 'new' && !isNaN(Number(assetId))) {
                    const numId = Number(assetId);
                    if (this.selectedContentAssetStore.asset()?.id !== numId) {
                        this.selectedContentAssetStore.getAsset({ id: numId });
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                } else if (assetId === 'new') {
                    if (this.selectedContentAssetStore.asset() !== undefined) {
                        this.selectedContentAssetStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(true);
                } else {
                    if (this.selectedContentAssetStore.asset() !== undefined) {
                        this.selectedContentAssetStore.reset();
                    }
                    this.#validationStateService.resetValidityOfSections(false);
                }
            },
            { debugName: 'sync content asset store with route assetId' }
        );

        effect(
            () => {
                const routeData = this.#route.snapshot.data as CompassRouteParent;
                const invalidSections = this.#validationStateService.invalidSections();

                this.registerInvalidSections(routeData, invalidSections);
            },
            { debugName: 'Register content library `invalidSections` under given criteria key' }
        );
        effect(
            () => {
                const asset = this.selectedContentAssetStore.asset();
                const assetId = this.routingService.assetId();

                if (asset && asset.id === Number(assetId)) {
                    this.validateSectionsOnLoad(asset, this.#validationService, this.#validationStateService);
                }
            },
            { debugName: 'content library validation (on load)' }
        );

        rxEffect(
            combineLatest([
                this.selectedContentAssetStore.asset$.pipe(
                    map(asset => asset?.folderId),
                    filterNil()
                ),
                this.contentAssetListStore.usersAvailableDivisions$,
                this.contentAssetListStore.selectedIamDivision$.pipe(filterNil()),
            ]).pipe(
                map(([folderId, usersAvailableDivisions, selectedIamDivision]) => ({
                    folderId,
                    usersAvailableDivisions,
                    selectedIamDivision,
                }))
            ),
            res => {
                this.contentAssetListStore.getCurrentFolder(res);
            }
        );
    }

    breadcrumbs: Signal<Breadcrumb[]> = computed(() => {
        const currentFolder = this.contentAssetListStore.vm().currentFolder;
        return buildFolderBreadcrumbs(currentFolder, `/${AppConstants.ContentLibraryComponent.path}`, true);
    });

    registerInvalidSections(routeData: CompassRouteParent, invalidSections: ContentLibrarySectionName[]) {
        this.#syncValidationStateService.registerInvalidSections(routeData, invalidSections);
    }

    validateSectionsOnLoad(
        asset: ContentAsset | undefined,
        validationService: ContentLibraryValidationService,
        validationStateService: ContentLibraryValidationStateService
    ) {
        this.#syncValidationStateService.validateSectionsOnLoad(asset, validationService, validationStateService);
    }

    containerTitle: Signal<string> = computed(() => {
        if (!this.showCloseButton()) {
            return AppConstants.ContentLibraryListComponent.containerTitle;
        }

        const currentChildPath = this.routingService.currentRoutePieces().routeWithoutParams?.split('/').at(-1);
        let subTitle =
            AppConstants.ContentLibraryComponent.children.find(child => child.path === currentChildPath)
                ?.containerTitle ?? '';

        if (currentChildPath === 'new') {
            subTitle = 'Edit Rule';
        }

        if (this.routingService.assetId() === 'new') {
            return `New Content Asset - ${subTitle}`;
        }

        const asset = this.selectedContentAssetStore.asset();
        const entityName = asset?.name || 'Content Asset';
        return `${entityName} - ${subTitle}`;
    });

    showCloseButton: Signal<boolean> = computed(() => {
        return this.routingService.parentSegment() !== '';
    });

    onClosed() {
        const folderId = this.selectedContentAssetStore.asset()?.folderId ?? AppConstants.defaultRootFolderId;
        this.selectedContentAssetStore.reset();
        this.#router.navigate(['.'], {
            relativeTo: this.#route,
            queryParams: { folderId: folderId },
        });
    }
}
